import { useCallback, useEffect, useState } from 'react'
import { supabase, readableError } from '../../lib/supabase'
import { Shell } from '../../components/Shell'
import { Spinner } from '../../components/ui'
import { useSession } from '../../lib/session'

/**
 * What people have written in from /support/:kind.
 *
 * The table, the statuses and the superadmin policies have existed since
 * migration 30; this screen is the half that was never built, which is why
 * every report until now was only visible in Sentry or in SQL.
 *
 * Deliberately not the moderation queue. That one is about a *song* and needs
 * three checks and a quorum. This is a mailbox: read it, deal with it, close
 * it. The only decision here is whether it is still your problem.
 */

const KIND = { issue: 'Broken', feature: 'Wanted' }
const STATUS = { open: 'Open', read: 'Read', closed: 'Closed' }

const ago = (iso) => {
  if (!iso) return null
  const h = Math.floor((Date.now() - new Date(iso)) / 3.6e6)
  if (h < 1) return 'just now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const named = (u) =>
  !u ? 'Someone' : [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'Someone'

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'issue', label: 'Broken' },
  { key: 'feature', label: 'Wanted' },
  { key: 'all', label: 'Everything' },
]

const matches = (r, f) =>
  f === 'all' ? true
  : f === 'open' ? r.status !== 'closed'
  : r.kind === f

export default function Feedback() {
  const { user } = useSession()
  const [rows, setRows] = useState(null)
  const [filter, setFilter] = useState('open')
  const [open, setOpen] = useState(null)
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState(null)
  // Keyed by row, so switching rows mid-sentence doesn't lose what was typed.
  const [draft, setDraft] = useState({})

  // Same stark treatment as Moderation, and for the same reason: this is
  // evidence being read at a desk, not a writer's app at 11pm.
  useEffect(() => {
    document.body.classList.add('modplain')
    return () => document.body.classList.remove('modplain')
  }, [])

  const load = useCallback(async () => {
    setErr(null)
    // Everything the row needs in one query. RLS narrows this to superadmins
    // on its own ("superadmin reads all feedback"), so no client filter is
    // load-bearing — a non-staff account gets an empty list, not a leak.
    const { data, error } = await supabase.from('feedback')
      .select('id, kind, scope, subject, body, status, created_at, handled_at, path, app_version, user_agent, reply_body, replied_at, replied_by, replier:replied_by(first_name, last_name), sender:user_id(first_name, last_name, email), church:church_id(name)')
      .order('created_at', { ascending: false })
      .limit(500)
    if (error) setErr(readableError(error))
    setRows(data ?? [])
  }, [])

  useEffect(() => { load() }, [load])

  const setStatus = async (row, status) => {
    setBusy(`${row.id}-${status}`); setErr(null)
    const { error } = await supabase.from('feedback')
      .update({
        status,
        // Only stamped when it is actually finished. "Read" is a bookmark, not
        // a resolution, and handled_at recording a glance would make the
        // column useless for the one question it answers.
        handled_at: status === 'closed' ? new Date().toISOString() : null,
        handled_by: status === 'closed' ? user?.id : null,
      })
      .eq('id', row.id)
    setBusy(null)
    if (error) return setErr(readableError(error))
    load()
  }

  // Send the reply through the edge function, never straight to the table.
  //
  // The columns are a record that an email actually left, so the browser is
  // not the thing that gets to assert one did: send-support-reply hands the
  // message to Resend first and writes reply_body afterwards with the service
  // role. It also owns the From address — the reason this replaced a mailto:
  // was that the desktop client picked the account, and support was answered
  // from a personal Gmail.
  const sendReply = async (row) => {
    const body = (draft[row.id] ?? '').trim()
    if (!body) return
    setBusy(`${row.id}-reply`); setErr(null)
    const { data, error } = await supabase.functions.invoke('send-support-reply', {
      body: { id: row.id, body },
    })
    setBusy(null)
    if (error || data?.error) return setErr(readableError(error ?? data.error))
    // Sent but not recorded. Say so rather than clearing the box: the reply is
    // gone either way, and the one wrong move now is sending it twice.
    if (data?.recorded === false) {
      setErr('Sent, but we could not record it here. Do not send it again.')
      return
    }
    setDraft((d) => ({ ...d, [row.id]: '' }))
    load()
  }

  // Opening one is the same act as having read it. Making "read" a separate
  // button people have to remember is how a queue ends up permanently full of
  // things that were, in fact, read.
  const reveal = (row) => {
    const next = open === row.id ? null : row.id
    setOpen(next)
    if (next && row.status === 'open') setStatus(row, 'read')
  }

  if (rows === null) return <Shell><Spinner /></Shell>

  const shown = rows.filter((r) => matches(r, filter))
  const unread = rows.filter((r) => r.status === 'open').length

  return (
    <Shell>
      <h2 className="m-h1">Support</h2>
      <p className="m-lede">
        <b>{rows.length}</b> in total · <b className={unread ? 'bad' : 'good'}>{unread}</b> not read
        yet · <b>{rows.filter((r) => r.status === 'closed').length}</b> closed
      </p>

      <div className="m-filters">
        {FILTERS.map((f) => (
          <button key={f.key} className={`m-filter${filter === f.key ? ' on' : ''}`}
                  onClick={() => setFilter(f.key)}>
            {f.label} ({rows.filter((r) => matches(r, f.key)).length})
          </button>
        ))}
      </div>

      {shown.map((r) => (
        <div key={r.id} className="m-row">
          <div className="m-rowtop">
            <span className={`m-score ${r.status === 'closed' ? 'good' : r.kind === 'issue' ? 'bad' : 'grey'}`}>
              {r.kind === 'issue' ? '!' : '+'}
            </span>
            <div className="m-who">
              <b>{r.subject}</b>
              <small>
                {[named(r.sender), r.church?.name, STATUS[r.status] ?? r.status]
                  .filter(Boolean).join(' · ')} · {ago(r.created_at)}
              </small>
            </div>
            <button className="m-view" onClick={() => reveal(r)}>
              {open === r.id ? 'Close' : 'Read'}
            </button>
          </div>

          <div className="m-tags">
            <span className={`m-tag${r.kind === 'issue' ? ' bad' : ''}`}>{KIND[r.kind] ?? r.kind}</span>
            {r.scope === 'church' && <span className="m-tag">Just for {r.church?.name ?? 'their church'}</span>}
            {r.status === 'open' && <span className="m-tag bad">Not read</span>}
            {r.path && <span className="m-tag">{r.path}</span>}
          </div>

          {open === r.id && (
            <div className="report">
              <h4 className="m-h4">What they wrote</h4>
              {/* Their words, wrapped as typed. A support report is often a
                  list of steps and reflowing it into a paragraph loses the
                  order, which is the part worth having. */}
              <p className="finding-why" style={{ whiteSpace: 'pre-wrap' }}>{r.body}</p>

              <h4 className="m-h4">Where they were</h4>
              <p className="m-small">
                {[r.path, r.app_version && `version ${r.app_version}`, r.sender?.email]
                  .filter(Boolean).join(' · ')}
              </p>
              {r.user_agent && <p className="m-small">{r.user_agent}</p>}

              <h4 className="m-h4">{r.reply_body ? 'What we sent back' : 'Your reply'}</h4>
              {r.reply_body ? (
                <>
                  <p className="finding-why" style={{ whiteSpace: 'pre-wrap' }}>{r.reply_body}</p>
                  <p className="m-small">
                    Sent by {named(r.replier)} {ago(r.replied_at)} · they can read it here too.
                  </p>
                </>
              ) : !r.sender?.email ? (
                <p className="m-small">
                  No email address on file for them, so there is nowhere to send a reply.
                </p>
              ) : (
                <>
                  <textarea
                    className="m-note"
                    rows={5}
                    maxLength={5000}
                    placeholder={`Reply to ${named(r.sender)}…`}
                    value={draft[r.id] ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                  />
                  <p className="m-small">
                    Sent from Beseter, not from you. Replies go to our support address, and
                    they will see this on their own support screen as well.
                  </p>
                </>
              )}

              <h4 className="m-h4">Your decision</h4>
              <div className="m-actions">
                {!r.reply_body && r.sender?.email && (
                  <button className="m-btn good" disabled={!!busy || !(draft[r.id] ?? '').trim()}
                          onClick={() => sendReply(r)}>
                    {busy === `${r.id}-reply` ? 'Sending…' : 'Send reply'}
                  </button>
                )}
                {r.status === 'closed' ? (
                  <button className="m-btn" disabled={!!busy} onClick={() => setStatus(r, 'read')}>
                    {busy === `${r.id}-read` ? 'Reopening…' : 'Reopen it'}
                  </button>
                ) : (
                  <button className={`m-btn${r.reply_body ? ' good' : ''}`} disabled={!!busy}
                          onClick={() => setStatus(r, 'closed')}>
                    {busy === `${r.id}-closed` ? 'Closing…' : 'Done — close it'}
                  </button>
                )}
              </div>
              {r.handled_at && <p className="m-small">Closed {ago(r.handled_at)}.</p>}
            </div>
          )}
        </div>
      ))}

      <p className="m-small" style={{ marginTop: 18 }}>
        {rows.length === 0
          ? 'Nothing yet. Every report sent from Support lands here.'
          : shown.length === 0 ? 'Nothing in this filter.' : `${shown.length} shown.`}
      </p>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
